import React from 'react';
import { formatNumber } from '../utils/format';
import { getFileDateLabel } from '../utils/documents';
import { useI18n } from '../i18n/useI18n';

const RedactionStatusBadge = ({ status, onAnnotate, disabled, t }) => {
  if (!status || status.loading) {
    return <span className="redactionStatusBadge redactionStatusBadgeUnknown">{t('docs.redaction.checking')}</span>;
  }
  if (status.hasRules) {
    return (
      <button type="button" className="redactionStatusBadge redactionStatusBadgeReady" onClick={onAnnotate} disabled={disabled}>
        {t('docs.redaction.annotated', { count: status.ruleCount })}
      </button>
    );
  }
  return (
    <button type="button" className="redactionStatusBadge redactionStatusBadgePending" onClick={onAnnotate} disabled={disabled}>
      {disabled ? t('docs.redaction.parseFirst') : t('docs.redaction.notAnnotated')}
    </button>
  );
};

export const DocumentsWorkspace = ({
  selectionSummary,
  extractionFailures,
  handleFileChange,
  documentTree,
  expandedYears,
  expandedMonths,
  toggleYear,
  toggleMonth,
  previewStoredDocument,
  deleteStoredDocument,
  selectedFiles,
  fileGroups,
  extractionItems,
  previewSelectedFile,
  removeSelectedFile,
  redactionStatusByFile,
  onAnnotateFile,
  isFileArchived
}) => {
  const { t } = useI18n();
  return (
  <div className="documentWorkspace">
    <div className="panel intakeMain nestedPanel">
      <div className="panelHeader compactHeader">
        <span className="panelKicker">Choose Source</span>
        <h2>{t('docs.title')}</h2>
      </div>
      <label className="uploadCard">
        <span>{t('docs.selectPdf')}</span>
        <input type="file" multiple accept=".pdf,application/pdf" onChange={handleFileChange} />
      </label>

      {selectionSummary ? (
        <div className="selectionPanel">
          <div className="dataRow"><span>{t('docs.selectedFiles')}</span><strong>{selectionSummary.total_files}</strong></div>
          <div className="dataRow"><span>{t('docs.pdfFiles')}</span><strong>{selectionSummary.pdf_files}</strong></div>
        </div>
      ) : null}
      {extractionFailures.length ? (
        <div className="selectionPanel">
          <div className="dataRow"><span>{t('docs.failedPages')}</span><strong>{extractionFailures.length}</strong></div>
          <div className="dataRow"><span>{t('docs.recentFailure')}</span><strong>{`${extractionFailures[0].source_file}${extractionFailures[0].source_page ? ` P${extractionFailures[0].source_page}` : ''}`}</strong></div>
        </div>
      ) : null}
    </div>

    {selectedFiles.length ? (
      <div className="panel pendingReview nestedPanel">
        <div className="panelHeader compactHeader">
          <span className="panelKicker">Current Selection</span>
          <h2>{t('docs.currentSelection')}</h2>
        </div>
        <p className="pendingReviewHint">{t('docs.reviewHint')}</p>
        <div className="pendingReviewList">
          {Object.keys(fileGroups).length ? Object.entries(fileGroups).map(([period, files]) => (
            <div key={period} className="fileGroupBlock">
              <div className="fileGroupTitle">{period}</div>
              <div className="fileRecordTree">
                {files.map((file) => {
                  const archived = isFileArchived(file.name);
                  return (
                    <div key={`${period}-${file.name}`} className="fileRecordCard treeNode">
                      <div className="fileRecordMeta">
                        <strong>{file.name}</strong>
                        <span>{getFileDateLabel(file, extractionItems, t)}</span>
                        <span>{formatNumber(file.size / 1024 / 1024, 2)} MB</span>
                        <RedactionStatusBadge
                          status={redactionStatusByFile[file.name]}
                          onAnnotate={() => onAnnotateFile(file)}
                          disabled={!archived}
                          t={t}
                        />
                      </div>
                      <div className="fileRecordActions">
                        <button className="ghostButton" onClick={() => previewSelectedFile(file)}>{t('docs.preview')}</button>
                        <button className="ghostButton dangerButton" onClick={() => removeSelectedFile(file.name)}>{t('docs.remove')}</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )) : null}
        </div>
      </div>
    ) : null}

    <div className="panel fileLibrary nestedPanel">
      <div className="panelHeader compactHeader">
        <span className="panelKicker">File Library</span>
        <h2>{t('docs.fileLibrary')}</h2>
      </div>

      <div className="fileLibraryTreeScroll">
        {Object.keys(documentTree).length ? Object.entries(documentTree).map(([year, monthMap]) => (
          <div key={year} className="treeYearBlock">
            <button className="treeToggle treeYearTitle" onClick={() => toggleYear(year)}>
              <span>{year}</span>
              <span className="treeToggleMeta">
                <em>{t('docs.count', { count: Object.values(monthMap).reduce((sum, monthDocuments) => sum + monthDocuments.length, 0) })}</em>
                <strong>{expandedYears[year] ? t('docs.collapse') : t('docs.expand')}</strong>
              </span>
            </button>
            {expandedYears[year] ? <div className="treeMonthList">
              {Object.entries(monthMap).map(([month, monthDocuments]) => {
                const monthKey = `${year}-${month}`;
                const expanded = !!expandedMonths[monthKey];
                return (
                <div key={`${year}-${month}`} className="treeMonthBlock">
                  <button className="treeToggle treeMonthTitle" onClick={() => toggleMonth(year, month)}>
                    <span>{month}</span>
                    <span className="treeToggleMeta">
                      <em>{t('docs.count', { count: monthDocuments.length })}</em>
                      <strong>{expanded ? t('docs.collapse') : t('docs.expand')}</strong>
                    </span>
                  </button>
                  {expanded ? <div className="fileRecordTree persistedTree">
                    {monthDocuments.map((document) => (
                      <div key={`${document.id}-${document.file_name}`} className="fileRecordCard treeNode">
                        <div className="fileRecordMeta">
                          <strong>{document.file_name}</strong>
                          <span>{document.business_date || document.uploaded_at || t('docs.dateUnidentified')}</span>
                          <span>{formatNumber(document.file_size / 1024 / 1024, 2)} MB</span>
                        </div>
                        <div className="fileRecordActions">
                          <button className="ghostButton" onClick={() => previewStoredDocument(document.id, document.file_name)}>{t('docs.preview')}</button>
                          <button className="ghostButton dangerButton" onClick={() => deleteStoredDocument(document.id, document.file_name)}>{t('docs.delete')}</button>
                        </div>
                      </div>
                    ))}
                  </div> : null}
                </div>
              );})}
            </div> : null}
          </div>
        )) : <div className="emptyState">{t('docs.empty')}</div>}
      </div>
    </div>
  </div>
  );
};

export default DocumentsWorkspace;
